import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Button from '@renderer/components/Button'
import { useAutoUpdate } from '@renderer/hooks/useAutoUpdate'
import { skipLaunchInstall } from '@renderer/lib/runtime'

/**
 * The app opened with an update an earlier run downloaded. It installs when the
 * countdown runs out and the app opens again on the new version; an operator who
 * needs the booth right now skips it and the old version carries on.
 *
 * Renders nothing outside Electron and in every other updater phase.
 */
const UpdateLaunchPrompt: React.FC = () => {
  const { t } = useTranslation()
  const { state } = useAutoUpdate()
  const [now, setNow] = useState(() => Date.now())
  const [skipping, setSkipping] = useState(false)

  const installAt = state?.phase === 'install-countdown' ? state.installAt : null

  useEffect(() => {
    if (installAt === null) return undefined
    setSkipping(false)
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 250)
    return (): void => clearInterval(timer)
  }, [installAt])

  if (installAt === null) return null

  const seconds = Math.max(0, Math.ceil((installAt - now) / 1000))

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-launch-title"
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-5 sm:p-8"
    >
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl sm:p-8">
        <h2 id="update-launch-title" className="mb-4 text-2xl font-bold">
          {t('settings.autoUpdate.launchPrompt.title', { version: state?.availableVersion ?? '' })}
        </h2>
        <p className="mb-2 text-gray-600">{t('settings.autoUpdate.launchPrompt.body')}</p>
        <p className="mb-8 text-gray-600" aria-live="polite">
          {t('settings.autoUpdate.launchPrompt.countdown', { seconds })}
        </p>
        <div className="flex justify-end">
          <Button
            text={t('settings.autoUpdate.launchPrompt.skip')}
            variant="secondary"
            disabled={skipping}
            onClick={async () => {
              setSkipping(true)
              await skipLaunchInstall()
            }}
            className="!min-h-[44px] !px-6 !py-2 !text-base"
          />
        </div>
      </div>
    </div>
  )
}

export default UpdateLaunchPrompt
